import { useAuth0 } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { BlankScreen } from '@strata/react-page-templates';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { fmhAPI } from 'api/APIBase';
import { getFmhToken } from 'api/authAPI';
import { clearDataForLogout } from 'services/authHelpers';
import { LOGIN_REDIRECT_KEY } from 'services/constants';
import { handleError } from 'services/Error';
import { initializePendo } from 'services/pendoUtils';
import { AppRoutes } from 'services/routes';
import { SocketTypes, wsConnect } from 'services/socketHelpers';
import { useAppDispatch } from 'store';
import { setAccessToken } from 'store/auth/authSlice';

/**
 * Component to be rendered on callback from Auth0.
 * Handles authentication of the user and redirects to /home.
 */
export const AuthCallback = () => {
    const { isLoading, user, getAccessTokenSilently, logout } = useAuth0();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        void (async () => {
            if (!isLoading && !!user) {
                try {
                    const fmhTokenResult = await getFmhToken({
                        accessToken: (await getAccessTokenSilently())!,
                    });

                    const accessToken = fmhTokenResult.access_token;
                    fmhAPI.base = fmhAPI.base.auth(`Bearer ${accessToken}`);

                    dispatch(
                        setAccessToken({
                            accessToken,
                            segmentIds: fmhTokenResult.segment_ids,
                            permissions: fmhTokenResult.permissions,
                        }),
                    );

                    dispatch(wsConnect({ socketType: SocketTypes.NOTIFICATION }));

                    datadogLogs.logger.info('User Authenticated', { user });
                    initializePendo(fmhTokenResult, user);
                    const redirectPath = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
                    navigate(redirectPath ?? AppRoutes.home);
                } catch (err) {
                    handleError('Error authenticating.', err as Error);
                    clearDataForLogout();
                    await logout({
                        logoutParams: {
                            returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
                        },
                    });
                }
            }
        })();
    }, [isLoading, user, dispatch, getAccessTokenSilently, navigate, logout]);

    return <BlankScreen />;
};
